import React from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Clock, Calendar, Users, Film } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Contact = () => {
  const socialLinks = [
    {
      name: 'Facebook',
      href: 'https://facebook.com/gaffindia',
      icon: Facebook
    },
    {
      name: 'Twitter',
      href: 'https://x.com/gaff_india',
      icon: () => (
        <svg 
          viewBox="0 0 24 24" 
          className="w-5 h-5"
          fill="currentColor"
        >
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      )
    },
    {
      name: 'Instagram',
      href: 'https://instagram.com/gaffindia',
      icon: Instagram
    }
  ];

  const festivalInfo = [
    {
      icon: Calendar,
      title: "Annual Event",
      description: "Celebrating cinema every year with new perspectives and stories"
    },
    {
      icon: Film,
      title: "Asian Cinema",
      description: "Showcasing the finest films from across the Asian continent"
    },
    {
      icon: Users,
      title: "Global Community",
      description: "Bringing together filmmakers, artists, and cinema enthusiasts"
    },
    {
      icon: Clock,
      title: "Multi-Day Festival",
      description: "A comprehensive celebration of cinematic arts"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      {/* Hero Section with Enhanced Design */}
      <section className="relative py-20 lg:py-32 overflow-hidden pt-32">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/30 via-black/80 to-black"></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/20 border border-red-500/30 mb-6">
                <Mail className="w-10 h-10 text-red-500" />
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-red-500 via-red-600 to-red-500 bg-clip-text text-transparent">
              Get in Touch
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Connect with the Guwahati Asian Film Festival and be part of a cinematic journey celebrating Asian cinema
            </p>
            
            {/* Social Links in Hero */}
            <div className="flex justify-center gap-4">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center hover:bg-red-500 hover:border-red-500 transition-all duration-300 group"
                    aria-label={social.name}
                  >
                    <IconComponent className="text-white group-hover:scale-110 transition-transform" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Reach Out to Us</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              We're here to answer your questions and help you connect with the world of Asian cinema
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Location Card */}
            <div className="group relative bg-gradient-to-br from-red-900/20 to-red-800/10 border border-red-500/20 rounded-2xl p-8 hover:border-red-500/40 transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-6 group-hover:bg-red-500/30 transition-colors">
                  <MapPin className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Location</h3>
                <p className="text-gray-300 leading-relaxed">Guwahati, Assam, India</p>
                <p className="text-gray-400 text-sm mt-2">Heart of Northeast India</p>
              </div>
            </div>

            {/* Email Card */}
            <div className="group relative bg-gradient-to-br from-red-900/20 to-red-800/10 border border-red-500/20 rounded-2xl p-8 hover:border-red-500/40 transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-6 group-hover:bg-red-500/30 transition-colors">
                  <Mail className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Email</h3>
                <a 
                  href="mailto:contact@gaff.co.in" 
                  className="text-gray-300 hover:text-red-400 transition-colors leading-relaxed"
                >
                  contact@gaff.co.in
                </a>
                <p className="text-gray-400 text-sm mt-2">We'll respond within 24 hours</p>
              </div>
            </div>

            {/* Phone Card */}
            <div className="group relative bg-gradient-to-br from-red-900/20 to-red-800/10 border border-red-500/20 rounded-2xl p-8 hover:border-red-500/40 transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-6 group-hover:bg-red-500/30 transition-colors">
                  <Phone className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Phone</h3>
                <a 
                  href="tel:+919577957657" 
                  className="text-gray-300 hover:text-red-400 transition-colors leading-relaxed"
                >
                  +91 9577 957 657
                </a>
                <p className="text-gray-400 text-sm mt-2">Mon-Fri, 10AM-6PM IST</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Festival Information Section */}
      <section className="py-20 border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">About GAFF</h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              The Guwahati Asian Film Festival is a celebration of cinematic excellence, 
              bringing together diverse voices and stories from across the Asian continent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {festivalInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/10 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{info.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{info.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-red-800/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Join Our Cinematic Journey
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Follow us on social media to stay updated with the latest news, 
              film selections, and festival announcements
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://facebook.com/gaffindia"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors duration-300 uppercase tracking-wider"
              >
                Follow on Facebook
              </a>
              <a
                href="https://instagram.com/gaffindia"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-semibold rounded-lg transition-all duration-300 uppercase tracking-wider"
              >
                Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Contact;
